import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  Layers, CheckCircle2, Send, Loader2, ArrowLeft, ExternalLink,
  Building2, Hash, FileText,
} from 'lucide-react';


const STATUS_TONE = {
  draft:     'bg-slate-100 text-slate-700',
  approved:  'bg-emerald-100 text-emerald-800',
  executing: 'bg-sky-100 text-sky-800',
  completed: 'bg-indigo-100 text-indigo-800',
};

const VOUCHER_STATUS_TONE = {
  draft:        'bg-slate-100 text-slate-700',
  approved:     'bg-emerald-100 text-emerald-800',
  sent_to_bank: 'bg-sky-100 text-sky-800',
  paid:         'bg-indigo-100 text-indigo-800',
  reconciled:   'bg-violet-100 text-violet-800',
};


export function PaymentRunsList() {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axiosInstance.get('/payment-runs');
        setRuns(data.runs || []);
      } catch { /* ignore */ } finally { setLoading(false); }
    })();
  }, []);
  return (
    <div className="min-h-screen bg-slate-100 p-4" dir="rtl" data-testid="payment-runs-list">
      <div className="max-w-[1400px] mx-auto">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-xl font-bold text-slate-800 inline-flex items-center gap-2">
            <Layers className="w-5 h-5 text-indigo-600" />
            دُفَعات الدَّفع
          </h1>
          <Link to="/payment-queue"
                 className="text-xs font-semibold rounded-lg border border-emerald-300
                            text-emerald-700 hover:bg-emerald-50 px-3 py-2
                            inline-flex items-center gap-1">
            قائِمَة الانتِظار
          </Link>
        </div>
        <div className="rounded-xl border bg-white overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400 mx-auto" />
            </div>
          ) : runs.length === 0 ? (
            <div className="p-8 text-center text-slate-400 text-xs">
              لا دُفَعات بَعد
            </div>
          ) : (
            <table className="w-full text-[11px]">
              <thead className="bg-slate-50 border-b text-slate-600">
                <tr>
                  <th className="text-right py-2 px-3">رَقم الدُّفعَة</th>
                  <th className="text-right py-2 px-3">الحالَة</th>
                  <th className="text-right py-2 px-3">الفَواتير</th>
                  <th className="text-right py-2 px-3">المُوَرِّدون</th>
                  <th className="text-left py-2 px-3">الإجمالي</th>
                  <th className="text-right py-2 px-3">تاريخ الإنشاء</th>
                  <th className="py-2 px-3"></th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => (
                  <tr key={r.id} className="border-b hover:bg-slate-50"
                      data-testid={`run-row-${r.id}`}>
                    <td className="py-2 px-3 font-mono font-semibold">{r.run_no}</td>
                    <td className="py-2 px-3">
                      <span className={`text-[10px] font-semibold rounded px-1.5 py-0.5 ${STATUS_TONE[r.status]}`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="py-2 px-3 font-mono">{r.bill_count}</td>
                    <td className="py-2 px-3 font-mono">{r.supplier_count}</td>
                    <td className="text-left py-2 px-3 font-mono font-semibold text-emerald-700">
                      {Number(r.total_amount || 0).toLocaleString('en')}
                    </td>
                    <td className="py-2 px-3 font-mono text-[10px] text-slate-500">
                      {r.created_at?.slice(0, 16).replace('T', ' ')}
                    </td>
                    <td className="py-2 px-3">
                      <Link to={`/payment-runs/${r.id}`}
                             data-testid={`run-open-${r.id}`}
                             className="text-indigo-600 inline-flex items-center gap-1">
                        <ExternalLink className="w-3 h-3" /> فَتح
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}


export default function PaymentRunDetail() {
  const { runId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [payingId, setPayingId] = useState(null);
  const [bankRef, setBankRef] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await axiosInstance.get(`/payment-runs/${runId}`);
      setData(d);
    } catch (e) {
      toast.error('فَشَل التَّحميل');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [runId]);

  const approve = async () => {
    setApproving(true);
    try {
      const { data: r } = await axiosInstance.post(
        `/payment-runs/${runId}/approve`);
      if (r.ok) {
        toast.success(`تَم اعتِماد الدُّفعَة — ${r.vouchers.length} سَنَد صَرف`);
        await load();
      } else {
        toast.error(r.error || 'فَشَل الاعتِماد');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فَشَل الاعتِماد');
    } finally { setApproving(false); }
  };

  const markPaid = async (voucherId) => {
    setPayingId(voucherId);
    try {
      const { data: r } = await axiosInstance.post(
        `/payment-vouchers/${voucherId}/mark-paid`,
        { bank_reference: bankRef[voucherId] || null });
      if (r.ok) {
        toast.success(`تَم الدَّفع — ${r.voucher_no}`);
        await load();
      } else {
        toast.error(r.error || 'فَشَل الدَّفع');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فَشَل الدَّفع');
    } finally { setPayingId(null); }
  };

  if (loading || !data?.run) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }
  const { run, vouchers } = data;
  return (
    <div className="min-h-screen bg-slate-100 p-4" dir="rtl" data-testid="payment-run-detail">
      <div className="max-w-[1400px] mx-auto">
        {/* Header */}
        <div className="mb-4">
          <button onClick={() => navigate('/payment-runs')}
                   data-testid="run-back-btn"
                   className="text-xs text-slate-500 hover:text-indigo-600 inline-flex items-center gap-1 mb-2">
            <ArrowLeft className="w-3.5 h-3.5" /> رُجوع لِلقائِمَة
          </button>
          <div className="flex items-center justify-between">
            <h1 className="text-lg font-bold text-slate-800 inline-flex items-center gap-2">
              <Layers className="w-5 h-5 text-indigo-600" />
              {run.run_no}
              <span className={`text-[10px] font-semibold rounded px-2 py-0.5 ${STATUS_TONE[run.status]}`}
                     data-testid="run-status">
                {run.status}
              </span>
            </h1>
            {run.status === 'draft' && (
              <button onClick={approve} disabled={approving}
                       data-testid="run-approve-btn"
                       className="text-xs font-bold rounded-lg bg-emerald-600
                                  hover:bg-emerald-700 text-white px-3 py-2
                                  inline-flex items-center gap-1 disabled:opacity-50">
                {approving
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <CheckCircle2 className="w-3.5 h-3.5" />}
                اعتِماد وَإنشاء سَنَدات الصَّرف
              </button>
            )}
          </div>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-4 gap-3 mb-4">
          <div className="rounded-xl border bg-white p-3">
            <div className="text-[10px] text-slate-500 uppercase">الفَواتير</div>
            <div className="text-lg font-bold" data-testid="run-bill-count">{run.bill_count}</div>
          </div>
          <div className="rounded-xl border bg-white p-3">
            <div className="text-[10px] text-slate-500 uppercase">المُوَرِّدون</div>
            <div className="text-lg font-bold" data-testid="run-supplier-count">{run.supplier_count}</div>
          </div>
          <div className="rounded-xl border bg-emerald-50 border-emerald-200 p-3">
            <div className="text-[10px] text-emerald-700 uppercase">الإجمالي</div>
            <div className="text-lg font-bold text-emerald-800 font-mono"
                 data-testid="run-total">
              {Number(run.total_amount).toLocaleString('en')}
            </div>
          </div>
          <div className="rounded-xl border bg-white p-3">
            <div className="text-[10px] text-slate-500 uppercase">تاريخ الدَّفع المُخَطَّط</div>
            <div className="text-sm font-mono">{run.planned_payment_date || '—'}</div>
          </div>
        </div>

        {/* Vouchers */}
        <div className="rounded-xl border bg-white overflow-hidden">
          <div className="bg-slate-100 px-3 py-2 border-b text-[11px] font-bold text-slate-700 inline-flex items-center gap-1">
            <FileText className="w-3.5 h-3.5" />
            سَنَدات الصَّرف ({vouchers?.length || 0})
          </div>
          {(!vouchers || vouchers.length === 0) ? (
            <div className="p-6 text-center text-slate-400 text-xs">
              لا سَنَدات بَعد — اعتَمِد الدُّفعَة لِتَوليدها.
            </div>
          ) : (
            <table className="w-full text-[11px]" data-testid="vouchers-table">
              <thead className="bg-slate-50 border-b text-slate-600">
                <tr>
                  <th className="text-right py-2 px-3">رَقم السَّنَد</th>
                  <th className="text-right py-2 px-3">المورد</th>
                  <th className="text-right py-2 px-3">الفَواتير</th>
                  <th className="text-left py-2 px-3">المَبلَغ</th>
                  <th className="text-right py-2 px-3">الحالَة</th>
                  <th className="text-right py-2 px-3">مَرجَع البَنك</th>
                  <th className="py-2 px-3">إجراء</th>
                </tr>
              </thead>
              <tbody>
                {vouchers.map((v) => (
                  <tr key={v.id} className="border-b" data-testid={`voucher-row-${v.id}`}>
                    <td className="py-2 px-3 font-mono font-semibold">{v.voucher_no}</td>
                    <td className="py-2 px-3">
                      <Building2 className="w-3 h-3 inline text-slate-400 mr-1" />
                      {v.supplier_name || '—'}
                    </td>
                    <td className="py-2 px-3 font-mono text-[10px]">
                      {v.bill_numbers?.join(', ')}
                    </td>
                    <td className="text-left py-2 px-3 font-mono font-semibold text-emerald-700">
                      {Number(v.amount).toLocaleString('en')}
                    </td>
                    <td className="py-2 px-3">
                      <span className={`text-[10px] font-semibold rounded px-1.5 py-0.5 ${VOUCHER_STATUS_TONE[v.status]}`}
                             data-testid={`voucher-status-${v.id}`}>
                        {v.status}
                      </span>
                    </td>
                    <td className="py-2 px-3 font-mono text-[10px]">
                      {v.bank_reference || '—'}
                    </td>
                    <td className="py-2 px-3">
                      {v.status === 'approved' && (
                        <div className="flex items-center gap-1">
                          <input
                            value={bankRef[v.id] || ''}
                            onChange={(e) => setBankRef({ ...bankRef,
                                                          [v.id]: e.target.value })}
                            placeholder="مَرجَع البَنك"
                            data-testid={`voucher-bank-ref-${v.id}`}
                            className="rounded border border-slate-300 px-1 py-0.5 text-[10px] w-28"
                          />
                          <button onClick={() => markPaid(v.id)}
                                   disabled={payingId === v.id}
                                   data-testid={`voucher-mark-paid-${v.id}`}
                                   className="text-[10.5px] font-semibold rounded bg-indigo-600
                                              hover:bg-indigo-700 text-white px-2 py-0.5
                                              inline-flex items-center gap-1 disabled:opacity-50">
                            {payingId === v.id
                              ? <Loader2 className="w-3 h-3 animate-spin" />
                              : <Send className="w-3 h-3" />}
                            دَفع
                          </button>
                        </div>
                      )}
                      {v.status === 'paid' && (
                        <span className="text-emerald-700 text-[10px] font-semibold">
                          ✓ مَدفوع
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
